import { tableBuilderActions } from '@constants/actionTypes';
import { isFormula, evaluateFormula, recalculateFormulas } from '@features/tableBuilder/formulaUtils';

const TEST_TABLE_JSON = JSON.parse(
  '[\n  {"rowIndex": 0, "columnIndex": 0, "value": "Company", "bold": true, "textColor": "#000000"},\n  {"rowIndex": 0, "columnIndex": 1, "value": "CEO", "textColor": "#000000"},\n  {"rowIndex": 0, "columnIndex": 2, "value": "CFO", "textColor": "#000000"},\n  {"rowIndex": 0, "columnIndex": 3, "value": "President/COO", "textColor": "#000000"},\n  {"rowIndex": 1, "columnIndex": 0, "value": "Microsoft (MSFT)"},\n  {"rowIndex": 1, "columnIndex": 1, "value": "Satya Nadella"},\n  {"rowIndex": 1, "columnIndex": 2, "value": "Amy Hood"},\n  {"rowIndex": 1, "columnIndex": 3, "value": "Bradford L. Smith (President)"},\n  {"rowIndex": 2, "columnIndex": 0, "value": "Apple (AAPL)"},\n  {"rowIndex": 2, "columnIndex": 1, "value": "Tim Cook"},\n  {"rowIndex": 2, "columnIndex": 2, "value": "Luca Maestri"},\n  {"rowIndex": 2, "columnIndex": 3, "value": "N/A"},\n  {"rowIndex": 3, "columnIndex": 0, "value": "Tesla (TSLA)"},\n  {"rowIndex": 3, "columnIndex": 1, "value": "Elon Musk"},\n  {"rowIndex": 3, "columnIndex": 2, "value": "Vaibhav Taneja"},\n  {"rowIndex": 3, "columnIndex": 3, "value": "N/A"},\n  {"rowIndex": 4, "columnIndex": 0, "value": "NVIDIA (NVDA)"},\n  {"rowIndex": 4, "columnIndex": 1, "value": "Jensen Huang"},\n  {"rowIndex": 4, "columnIndex": 2, "value": "Colette Kress"},\n  {"rowIndex": 4, "columnIndex": 3, "value": "N/A"},\n  {"rowIndex": 5, "columnIndex": 0, "value": "JPMorgan Chase (JPM)"},\n  {"rowIndex": 5, "columnIndex": 1, "value": "Jamie Dimon"},\n  {"rowIndex": 5, "columnIndex": 2, "value": "Jeremy Barnum"},\n  {"rowIndex": 5, "columnIndex": 3, "value": "N/A"}\n]'
);
const TEST_STYLE_TABLE_JSON = [
  {
    rowIndex: 0,
    columnIndex: 0,
    value: 'Bold Text',
    bold: true,
  },
  {
    rowIndex: 0,
    columnIndex: 1,
    value: 'Bold + Italic',
    bold: true,
    italic: true,
  },
  {
    rowIndex: 1,
    columnIndex: 0,
    value: 'Underlined',
    underline: true,
  },
  {
    rowIndex: 1,
    columnIndex: 1,
    value: 'Strikethrough',
    strike: true,
  },
  {
    rowIndex: 2,
    columnIndex: 0,
    value: 'All Styles',
    bold: true,
    italic: true,
    underline: true,
    strike: true,
  },
];
const TEST_TEXT_COLOR_TABLE_JSON = [
  {
    rowIndex: 0,
    columnIndex: 0,
    value: 'Red Text',
    textColor: '#ff0000',
  },
  {
    rowIndex: 0,
    columnIndex: 1,
    value: 'Yellow Background',
    backgroundColor: '#ffff00',
  },
  {
    rowIndex: 1,
    columnIndex: 0,
    value: 'White on Blue',
    textColor: '#ffffff',
    backgroundColor: '#0066cc',
  },
  {
    rowIndex: 1,
    columnIndex: 1,
    value: 'All Styles',
    bold: true,
    italic: true,
    underline: true,
    textColor: '#ff00ff',
    backgroundColor: '#ffffe0',
  },
];

// const defaultCurrentTableJSON = TEST_TABLE_JSON;
const defaultCurrentTableJSON = [];

const defaultState = {
  chatHistory: [],
  currentTableJSON: defaultCurrentTableJSON,
  chatError: null,
  currentResultAnswer: null,
  workingState: defaultCurrentTableJSON.length === 0 ? 'unstarted' : 'complete',
  metadata: {
    columnWidths: {}, // { columnIndex: width }
  },
};

export default function reducer(state = defaultState, action) {
  switch (action.type) {
    case tableBuilderActions.addChatHistory: {
      return {
        ...state,
        chatHistory: [...state.chatHistory, action.payload],
      };
    }

    case tableBuilderActions.clearChatHistory: {
      return {
        ...state,
        chatHistory: [],
      };
    }

    case tableBuilderActions.setCurrentTableJSON: {
      return {
        ...state,
        currentTableJSON: action.payload,
      };
    }

    case tableBuilderActions.updateChatError: {
      return {
        ...state,
        chatError: action.payload,
      };
    }

    case tableBuilderActions.clearCurrentTableJSON: {
      return {
        ...state,
        currentTableJSON: [],
      };
    }

    case tableBuilderActions.addCell: {
      return {
        ...state,
        currentTableJSON: [...state.currentTableJSON, action.payload],
      };
    }

    case tableBuilderActions.updateCell: {
      const { rowIndex, columnIndex, value } = action.payload;

      let cellUpdate = {};

      // Check if value is a formula
      if (isFormula(value)) {
        cellUpdate.formula = value;
        // Evaluate formula and store result in value
        // Create temporary table with the formula set to evaluate it
        const tempTable = state.currentTableJSON.map((cell) =>
          cell.rowIndex === rowIndex && cell.columnIndex === columnIndex ? { ...cell, formula: value } : cell
        );
        cellUpdate.value = evaluateFormula(value, tempTable, rowIndex, columnIndex);
      } else {
        // Not a formula - clear any existing formula and set value directly
        cellUpdate.value = value;
        cellUpdate.formula = undefined;
      }

      // Update the cell
      const updatedTable = state.currentTableJSON.map((cell) =>
        cell.rowIndex === rowIndex && cell.columnIndex === columnIndex ? { ...cell, ...cellUpdate } : cell
      );

      // Recalculate all formulas in case this change affects other cells
      const recalculatedTable = recalculateFormulas(updatedTable);

      return {
        ...state,
        currentTableJSON: recalculatedTable,
      };
    }

    case tableBuilderActions.deleteCell: {
      return {
        ...state,
        currentTableJSON: state.currentTableJSON.filter((cell) => cell.id !== action.payload.id),
      };
    }

    case tableBuilderActions.addRow: {
      return {
        ...state,
        currentTableJSON: [...state.currentTableJSON, action.payload],
      };
    }

    case tableBuilderActions.deleteRow: {
      return {
        ...state,
        currentTableJSON: state.currentTableJSON.filter((row) => row.id !== action.payload.id),
      };
    }

    case tableBuilderActions.deleteRowByIndex: {
      const { rowIndex } = action.payload;

      // Remove all cells in the row and shift cells below up
      const updatedCells = state.currentTableJSON
        .filter((cell) => cell.rowIndex !== rowIndex)
        .map((cell) => {
          if (cell.rowIndex > rowIndex) {
            return { ...cell, rowIndex: cell.rowIndex - 1 };
          }
          return cell;
        });

      return {
        ...state,
        currentTableJSON: updatedCells,
      };
    }

    case tableBuilderActions.insertRowAt: {
      const { rowIndex } = action.payload;

      // Shift all cells at or after the insert position down
      const updatedCells = state.currentTableJSON.map((cell) => {
        if (cell.rowIndex >= rowIndex) {
          return { ...cell, rowIndex: cell.rowIndex + 1 };
        }
        return cell;
      });

      // Find the maximum column index to determine how many columns exist
      const maxCol = updatedCells.length > 0 ? Math.max(...updatedCells.map((c) => c.columnIndex)) + 1 : 1;

      // Add placeholder cells for all columns in the new row
      const placeholderCells = [];
      for (let columnIndex = 0; columnIndex < maxCol; columnIndex++) {
        placeholderCells.push({
          rowIndex,
          columnIndex,
          value: '',
        });
      }

      const finalCells = [...updatedCells, ...placeholderCells];

      return {
        ...state,
        currentTableJSON: finalCells,
      };
    }

    case tableBuilderActions.addColumn: {
      return {
        ...state,
        currentTableJSON: [...state.currentTableJSON, action.payload],
      };
    }

    case tableBuilderActions.deleteColumn: {
      return {
        ...state,
        currentTableJSON: state.currentTableJSON.filter((column) => column.id !== action.payload.id),
      };
    }

    case tableBuilderActions.deleteColumnByIndex: {
      const { columnIndex } = action.payload;

      // Remove all cells in the column and shift cells to the right leftward
      const updatedCells = state.currentTableJSON
        .filter((cell) => cell.columnIndex !== columnIndex)
        .map((cell) => {
          if (cell.columnIndex > columnIndex) {
            return { ...cell, columnIndex: cell.columnIndex - 1 };
          }
          return cell;
        });

      // Shift column widths
      const updatedColumnWidths = {};
      Object.entries(state.metadata.columnWidths).forEach(([colIdx, width]) => {
        const idx = parseInt(colIdx);
        if (idx < columnIndex) {
          updatedColumnWidths[idx] = width;
        } else if (idx > columnIndex) {
          updatedColumnWidths[idx - 1] = width;
        }
        // Skip the deleted column
      });

      return {
        ...state,
        currentTableJSON: updatedCells,
        metadata: {
          ...state.metadata,
          columnWidths: updatedColumnWidths,
        },
      };
    }

    case tableBuilderActions.clearTableBuilder: {
      return {
        chatHistory: [],
        currentTableJSON: [],
        currentResultAnswer: null,
        chatError: null,
        workingState: 'unstarted',
        metadata: {
          columnWidths: {},
        },
      };
    }
    case tableBuilderActions.updateCurrentResultAnswer: {
      return {
        ...state,
        currentResultAnswer: action.payload,
      };
    }
    case tableBuilderActions.setWorkingState: {
      return {
        ...state,
        workingState: action.payload,
      };
    }

    case tableBuilderActions.updateMetadata: {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          ...action.payload,
        },
      };
    }

    case tableBuilderActions.setColumnWidth: {
      const { columnIndex, width } = action.payload;
      return {
        ...state,
        metadata: {
          ...state.metadata,
          columnWidths: {
            ...state.metadata.columnWidths,
            [columnIndex]: width,
          },
        },
      };
    }

    case tableBuilderActions.insertColumn: {
      const { columnIndex } = action.payload;
      // Shift all cells at or after the insert position to the right
      const updatedCells = state.currentTableJSON.map((cell) => {
        if (cell.columnIndex >= columnIndex) {
          return { ...cell, columnIndex: cell.columnIndex + 1 };
        }
        return cell;
      });

      // Find the maximum row index to determine how many rows exist
      const maxRow = updatedCells.length > 0 ? Math.max(...updatedCells.map((c) => c.rowIndex)) + 1 : 1;

      // Add placeholder cells for all rows in the new column
      const placeholderCells = [];
      for (let rowIndex = 0; rowIndex < maxRow; rowIndex++) {
        // Only add placeholder if no cell already exists at this position
        const hasExistingCell = updatedCells.some(
          (cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex
        );

        if (!hasExistingCell) {
          placeholderCells.push({
            rowIndex,
            columnIndex,
            value: '',
          });
        }
      }

      const finalCells = [...updatedCells, ...placeholderCells];

      // Shift column widths
      const updatedColumnWidths = {};
      Object.entries(state.metadata.columnWidths).forEach(([colIdx, width]) => {
        const idx = parseInt(colIdx);
        if (idx >= columnIndex) {
          updatedColumnWidths[idx + 1] = width;
        } else {
          updatedColumnWidths[idx] = width;
        }
      });

      return {
        ...state,
        currentTableJSON: finalCells,
        metadata: {
          ...state.metadata,
          columnWidths: updatedColumnWidths,
        },
      };
    }

    case tableBuilderActions.insertColumnAt: {
      const { columnIndex } = action.payload;
      // Shift all cells at or after the insert position to the right
      const updatedCells = state.currentTableJSON.map((cell) => {
        if (cell.columnIndex >= columnIndex) {
          return { ...cell, columnIndex: cell.columnIndex + 1 };
        }
        return cell;
      });

      // Find the maximum row index to determine how many rows exist
      const maxRow = updatedCells.length > 0 ? Math.max(...updatedCells.map((c) => c.rowIndex)) + 1 : 1;

      // Add placeholder cells for all rows in the new column
      const placeholderCells = [];
      for (let rowIndex = 0; rowIndex < maxRow; rowIndex++) {
        // Only add placeholder if no cell already exists at this position
        const hasExistingCell = updatedCells.some(
          (cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex
        );

        if (!hasExistingCell) {
          placeholderCells.push({
            rowIndex,
            columnIndex,
            value: '',
          });
        }
      }

      const finalCells = [...updatedCells, ...placeholderCells];

      // Shift column widths
      const updatedColumnWidths = {};
      Object.entries(state.metadata.columnWidths).forEach(([colIdx, width]) => {
        const idx = parseInt(colIdx);
        if (idx >= columnIndex) {
          updatedColumnWidths[idx + 1] = width;
        } else {
          updatedColumnWidths[idx] = width;
        }
      });

      return {
        ...state,
        currentTableJSON: finalCells,
        metadata: {
          ...state.metadata,
          columnWidths: updatedColumnWidths,
        },
      };
    }

    case tableBuilderActions.toggleCellBold: {
      const { rowIndex, columnIndex } = action.payload;
      // Find the cell and toggle its bold property
      const cellExists = state.currentTableJSON.some(
        (cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex
      );

      if (cellExists) {
        // Cell exists, toggle bold property
        return {
          ...state,
          currentTableJSON: state.currentTableJSON.map((cell) => {
            if (cell.rowIndex === rowIndex && cell.columnIndex === columnIndex) {
              return {
                ...cell,
                bold: !cell.bold,
              };
            }
            return cell;
          }),
        };
      } else {
        // Cell doesn't exist, create it with bold: true
        return {
          ...state,
          currentTableJSON: [
            ...state.currentTableJSON,
            {
              rowIndex,
              columnIndex,
              value: '',
              bold: true,
            },
          ],
        };
      }
    }

    case tableBuilderActions.toggleCellItalic: {
      const { rowIndex, columnIndex } = action.payload;
      const cellExists = state.currentTableJSON.some(
        (cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex
      );

      if (cellExists) {
        return {
          ...state,
          currentTableJSON: state.currentTableJSON.map((cell) => {
            if (cell.rowIndex === rowIndex && cell.columnIndex === columnIndex) {
              return {
                ...cell,
                italic: !cell.italic,
              };
            }
            return cell;
          }),
        };
      } else {
        return {
          ...state,
          currentTableJSON: [
            ...state.currentTableJSON,
            {
              rowIndex,
              columnIndex,
              value: '',
              italic: true,
            },
          ],
        };
      }
    }

    case tableBuilderActions.toggleCellUnderline: {
      const { rowIndex, columnIndex } = action.payload;
      const cellExists = state.currentTableJSON.some(
        (cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex
      );

      if (cellExists) {
        return {
          ...state,
          currentTableJSON: state.currentTableJSON.map((cell) => {
            if (cell.rowIndex === rowIndex && cell.columnIndex === columnIndex) {
              return {
                ...cell,
                underline: !cell.underline,
              };
            }
            return cell;
          }),
        };
      } else {
        return {
          ...state,
          currentTableJSON: [
            ...state.currentTableJSON,
            {
              rowIndex,
              columnIndex,
              value: '',
              underline: true,
            },
          ],
        };
      }
    }

    case tableBuilderActions.toggleCellStrike: {
      const { rowIndex, columnIndex } = action.payload;
      const cellExists = state.currentTableJSON.some(
        (cell) => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex
      );

      if (cellExists) {
        return {
          ...state,
          currentTableJSON: state.currentTableJSON.map((cell) => {
            if (cell.rowIndex === rowIndex && cell.columnIndex === columnIndex) {
              return {
                ...cell,
                strike: !cell.strike,
              };
            }
            return cell;
          }),
        };
      } else {
        return {
          ...state,
          currentTableJSON: [
            ...state.currentTableJSON,
            {
              rowIndex,
              columnIndex,
              value: '',
              strike: true,
            },
          ],
        };
      }
    }

    default:
      return state;
  }
}
